import { useEffect, useMemo, useState } from 'react';
import {
  IconAlertCircle, IconArrowDown, IconArrowUp, IconBuildingStore, IconCalendarStats, IconCash,
  IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight, IconCircleCheckFilled, IconCircleXFilled, IconClipboardCheck, IconCoins, IconCopy, IconDeviceDesktop,
  IconBell, IconBellOff, IconEdit, IconLink, IconListCheck, IconMapPin, IconMoon, IconPackage, IconPlug, IconPlus,
  IconReceipt, IconRefresh, IconSquareRoundedCheck, IconSun,
  IconTrash, IconX
} from '@tabler/icons-react';
import { REGIONS, bangkokDate, type Branch, type DailySale, type Region, type TabId } from './domain';
import { buildReportParts, thaiDisplayDate, totalsByRegion, type ReportParts } from './report';
import { recordEvent } from './sync';
import { useAppStore } from './useAppStore';
import { ReportSlipActions } from './ReportSlipImage';
import { disablePushNotifications, enablePushNotifications, readPushState, syncPushSubscription, type PushState } from './push';
import { ReportSubmissionError, submitThenCopy, type ReportMode } from './reportSubmission';

const money = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 2 });
const monthKey = (date: string) => date.slice(0, 7);

function haptic(ms = 15) {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(ms);
    }
  } catch {
    // Ignore if unsupported or restricted
  }
}

export function getSaleStatus(sale: DailySale, today: string) {
  if (sale.salesAmount === null) return { isToday: false, label: 'ยังไม่อัปเดต' };
  const updatedDate = bangkokDate(new Date(sale.updatedAt));
  if (updatedDate !== today) return { isToday: false, label: 'ยังไม่อัปเดต' };
  const timeStr = new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' }).format(new Date(sale.updatedAt));
  return { isToday: true, label: `วันนี้ ${timeStr}` };
}

export function App() {
  const store = useAppStore();
  const [tab, setTabState] = useState<TabId>(() => {
    const saved = sessionStorage.getItem('daily-active-tab');
    return saved === 'history' || saved === 'manage' ? saved : 'sales';
  });
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const [toast, setToast] = useState('');
  const [confirm, setConfirm] = useState<{ title: string; detail: string; action: () => void } | null>(null);
  const [report, setReport] = useState<ReportParts | null>(null);
  const [reportCopied, setReportCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const setTab = (next: TabId) => {
    sessionStorage.setItem('daily-active-tab', next);
    setTabState(next);
  };

  useEffect(() => {
    if (!report) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setReport(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [report]);

  const todaySales = useMemo(() => store.sales.filter((sale) => sale.salesDate === store.today), [store.sales, store.today]);
  const total = todaySales.reduce((sum, sale) => sum + (sale.salesAmount ?? 0), 0);

  const updatedTodayCount = useMemo(() => {
    return store.activeBranches.filter((branch) => getSaleStatus(store.saleFor(branch), store.today).isToday).length;
  }, [store.activeBranches, store.saleFor, store.today]);

  const notify = (message: string) => { setToast(message); window.setTimeout(() => setToast(''), 2600); };

  const toggleTheme = () => {
    const next = !dark; setDark(next); localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const scrollToBranch = (branchId: string) => {
    setTab('sales');
    window.setTimeout(() => {
      const el = document.getElementById(`branch-${branchId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-pulse');
        window.setTimeout(() => el.classList.remove('highlight-pulse'), 1800);
      }
    }, 100);
  };

  const copyReport = async () => {
    if (isSubmitting) return;
    if (!store.activeBranches.length) return notify('เพิ่มสาขาก่อนสร้างรายงาน');
    const missing = store.activeBranches.filter((branch) => store.saleFor(branch).salesAmount === null);
    if (missing.length) return notify(`กรอกยอดให้ครบก่อนส่งรายงาน (เหลือ ${missing.length} สาขา)`);
    haptic(35);
    const parts = buildReportParts(store.activeBranches, store.sales, store.today);
    const mode: ReportMode = store.reportSubmitted ? 'update' : 'create';
    setIsSubmitting(true);
    try {
      const outcome = await submitThenCopy(
        () => store.submitReport(mode),
        () => navigator.clipboard.writeText(parts.fullText)
      );
      if (outcome.copied) {
        await recordEvent('report_copied', { date: store.today, total, branchCount: store.activeBranches.length, textLength: parts.fullText.length });
      }
      setReportCopied(outcome.copied);
      setReport(parts);
      notify(outcome.copied
        ? (mode === 'create' ? 'ส่งและคัดลอกรายงานแล้ว' : 'อัปเดตและคัดลอกรายงานแล้ว')
        : 'ส่งรายงานแล้ว แต่คัดลอกไม่สำเร็จ');
    } catch (error) {
      if (error instanceof ReportSubmissionError && error.code === 'conflict') {
        notify('มีรายงานวันนี้แล้ว ยังไม่ได้คัดลอก—กดอัปเดตอีกครั้ง');
      } else {
        const message = error instanceof Error ? error.message : 'ส่งรายงานไม่สำเร็จ';
        notify(`${message} จึงยังไม่ได้คัดลอก`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!store.ready) return <div className="boot"><img className="boot-mark" src="/icons/daily-sales-192.png" alt="" /><p>กำลังเตรียมข้อมูลของคุณ</p></div>;

  const isAllCompleted = store.activeBranches.length > 0 && updatedTodayCount === store.activeBranches.length;

  return <div className={dark ? 'app theme-dark' : 'app'}>
    <header className="topbar">
      <div
        className="brand"
        onClick={() => {
          haptic(15);
          setShowAbout(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            haptic(15);
            setShowAbout(true);
          }
        }}
        aria-label="เกี่ยวกับ Daily Sales"
      >
        <img className="brand-mark" src="/icons/daily-sales-192.png" alt="" />
        <div><strong>Daily Sales</strong><span>{thaiDisplayDate(store.today)}</span></div>
      </div>
      <div className="header-metrics">
        <button
          type="button"
          className={`progress-pill-btn ${isAllCompleted ? 'all-done' : 'pending'}`}
          onClick={() => {
            haptic(12);
            setShowProgress(true);
          }}
          aria-label="ดูความคืบหน้าการลงยอด"
        >
          {isAllCompleted ? <IconListCheck /> : <IconAlertCircle />}
          <span>{updatedTodayCount}/{store.activeBranches.length} สาขา</span>
        </button>
      </div>
      <button className="icon-button" onClick={toggleTheme} aria-label="สลับธีม">{dark ? <IconSun /> : <IconMoon />}</button>
    </header>

    <main className="page-shell">
      {tab === 'sales' && <SalesPage {...store} isSubmitting={isSubmitting} onReset={() => setConfirm({ title: 'ล้างยอดของวันนี้?', detail: 'ยอดและสถานะตรวจงานของทุกสาขาวันนี้จะถูกล้าง แต่รายชื่อสาขายังอยู่', action: () => { void store.resetToday().then(() => notify('ล้างยอดวันนี้แล้ว')); } })} onCopy={() => void copyReport()} />}
      {tab === 'history' && <HistoryPage sales={store.sales} branches={store.branches} today={store.today} />}
      {tab === 'manage' && <ManagePage {...store} notify={notify} ask={setConfirm} />}
    </main>

    <nav className="bottom-nav" aria-label="เมนูหลัก">
      <NavButton active={tab === 'sales'} onClick={() => setTab('sales')} icon={<IconCash />} label="ยอดวันนี้" />
      <NavButton active={tab === 'history'} onClick={() => setTab('history')} icon={<IconCalendarStats />} label="ประวัติ" />
      <NavButton active={tab === 'manage'} onClick={() => setTab('manage')} icon={<IconBuildingStore />} label="สาขา" />
    </nav>

    {toast && <div className="toast"><IconCheck />{toast}</div>}
    {confirm && <Modal onClose={() => setConfirm(null)}><div className="modal-icon danger"><IconTrash /></div><h2>{confirm.title}</h2><p>{confirm.detail}</p><div className="modal-actions"><button className="button secondary" onClick={() => setConfirm(null)}>ยกเลิก</button><button className="button danger" onClick={() => { confirm.action(); setConfirm(null); }}>ยืนยัน</button></div></Modal>}
    {report && (
      <div className="report-overlay" role="dialog" aria-modal="true" aria-label="รายงานวันนี้">
        <header
          className="report-overlay-header"
          onTouchStart={(e) => setTouchStartY(e.touches[0].clientY)}
          onTouchEnd={(e) => {
            if (touchStartY !== null) {
              const deltaY = e.changedTouches[0].clientY - touchStartY;
              if (deltaY > 50) {
                haptic(15);
                setReport(null);
              }
              setTouchStartY(null);
            }
          }}
        >
          <div
            className="report-drag-pill"
            onClick={() => {
              haptic(12);
              setReport(null);
            }}
            role="button"
            tabIndex={0}
            aria-label="แตะหรือปัดลงเพื่อปิด"
          />
          <div className="report-overlay-header-inner">
            <div>
              <h2>รายงานวันนี้</h2>
              <span>{reportCopied ? 'ส่งและคัดลอกลงคลิปบอร์ดแล้ว' : 'ส่งรายงานแล้ว แต่คัดลอกไม่สำเร็จ'}</span>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                haptic(12);
                setReport(null);
              }}
              aria-label="ปิด"
            >
              <IconX />
            </button>
          </div>
        </header>

        <div className="report-overlay-body">
          <div className="slip-paper">
            <div className="slip-date-row">
              <IconCalendarStats />
              <span>สรุปยอดประจำวันที่ {report.dateStr}</span>
            </div>

            {report.branches.map((item, index) => (
              <div className="slip-branch-row" key={item.id}>
                <div className="slip-branch-head">
                  <h3 className="slip-branch-title">{index + 1}. สาขา{item.name}</h3>
                  <div className="slip-branch-amt">
                    <IconCash />
                    <span>ยอดวันนี้ <strong>฿{money.format(item.amount)}</strong></span>
                  </div>
                </div>
                <div className="slip-branch-checks">
                  {item.checks.map((chk, i) => (
                    <div key={i} className={`slip-check-line ${chk.ok ? 'ok' : 'no'}`}>
                      {chk.ok ? <IconCircleCheckFilled className="slip-chk-icon ok" /> : <IconCircleXFilled className="slip-chk-icon no" />}
                      <span>{chk.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="report-overlay-dock">
          <div className="slip-dock-inner">
            <div className="slip-total-line">
              <div className="slip-total-label">
                <IconCoins />
                <span>ยอดขายรวมทั้งหมด</span>
              </div>
              <strong>฿{money.format(report.total)}</strong>
            </div>
            <div className="slip-zone-section">
              <div className="slip-zone-title">
                <IconMapPin />
                <span>สรุปรวมตามโซนภาค</span>
              </div>
              <div className="slip-zone-list">
                {report.regionTotals.map(([region, amount]) => (
                  <div key={region} className="slip-zone-row">
                    <span className="slip-zone-tag"><IconMapPin /> {region}</span>
                    <strong>฿{money.format(amount)}</strong>
                  </div>
                ))}
              </div>
            </div>
            <ReportSlipActions
              branches={store.activeBranches}
              sales={store.sales}
              date={store.today}
              total={report.total}
              regionTotals={report.regionTotals}
              onClose={() => {
                haptic(15);
                setReport(null);
              }}
              onNotify={notify}
            />
          </div>
        </footer>
      </div>
    )}
    {showProgress && (
      <ProgressModal
        branches={store.activeBranches}
        saleFor={store.saleFor}
        today={store.today}
        onClose={() => setShowProgress(false)}
        onSelectBranch={scrollToBranch}
        onNotify={notify}
      />
    )}
    {showAbout && (
      <Modal compact onClose={() => setShowAbout(false)}>
        <div className="dev-card">
          <div className="dev-card-head">
            <div className="dev-term-badge">
              <span className="dev-term-dot" />
              <span>spec://dev</span>
            </div>
            <button type="button" className="dev-x-btn" onClick={() => setShowAbout(false)} aria-label="ปิด">
              <IconX />
            </button>
          </div>
          <div className="dev-term-body">
            <div className="dev-code-comment">// Core Engineering</div>
            <div className="dev-code-main">
              <div><span className="dev-kw">const</span> <span className="dev-prop">team</span> = <strong className="dev-val">"SeventyOne"</strong>;</div>
              <div><span className="dev-kw">console</span>.<span className="dev-prop">log</span>(<strong className="dev-val">"Hello World"</strong>);</div>
            </div>
            <div className="dev-code-meta">
              <div><span>repo</span><strong>Daily Sales</strong></div>
              <div><span>version</span><strong>{__APP_VERSION__}</strong></div>
            </div>
          </div>
        </div>
      </Modal>
    )}
  </div>;
}

function ProgressModal({ branches, saleFor, today, onClose, onSelectBranch, onNotify }: {
  branches: Branch[];
  saleFor: (branch: Branch) => DailySale;
  today: string;
  onClose: () => void;
  onSelectBranch: (branchId: string) => void;
  onNotify: (message: string) => void;
}) {
  const [copiedPending, setCopiedPending] = useState(false);
  const { pending, completed } = useMemo(() => {
    const p: Array<{ branch: Branch; sale: DailySale }> = [];
    const c: Array<{ branch: Branch; sale: DailySale; label: string }> = [];
    for (const branch of branches) {
      const sale = saleFor(branch);
      const status = getSaleStatus(sale, today);
      if (status.isToday) {
        c.push({ branch, sale, label: status.label });
      } else {
        p.push({ branch, sale });
      }
    }
    return { pending: p, completed: c };
  }, [branches, saleFor, today]);

  const handleCopyPending = async () => {
    if (pending.length === 0) return;
    haptic(25);
    const dayMonth = today.split('-').slice(1).reverse().join('/'); // DD/MM เช่น 20/09
    const lines = [
      `⚠️ สาขาที่ยังไม่ส่งยอดประจำวันที่ ${dayMonth}:`,
      '',
      ...pending.map(({ branch }) => `สาขา${branch.name}`),
      '',
      'รบกวนส่งยอดด้วยนะคะ 🙏'
    ];
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback
    }
    setCopiedPending(true);
    window.setTimeout(() => setCopiedPending(false), 2000);
    onNotify('คัดลอกข้อความทวงยอดแล้ว');
  };

  return (
    <Modal wide onClose={onClose}>
      <div className="modal-heading">
        <div>
          <span className="eyebrow">ความคืบหน้าประจำวัน</span>
          <h2>ตรวจสอบรายสาขา ({completed.length}/{branches.length})</h2>
        </div>
        <button className="icon-button" onClick={onClose} aria-label="ปิด"><IconX /></button>
      </div>

      <div className="progress-modal-grid">
        <div className="progress-col pending-col">
          <div className="progress-col-header">
            <div className="progress-col-title">
              <IconAlertCircle className="col-icon pending" />
              <h3>ยังไม่อัปเดตวันนี้ ({pending.length})</h3>
            </div>
            {pending.length > 0 && (
              <button
                type="button"
                className={`copy-pending-btn ${copiedPending ? 'copied' : ''}`}
                onClick={handleCopyPending}
                title="คัดลอกข้อความทวงยอด"
              >
                {copiedPending ? <IconCheck /> : <IconCopy />}
                <span>{copiedPending ? 'คัดลอกแล้ว' : 'คัดลอก'}</span>
              </button>
            )}
          </div>
          {pending.length === 0 ? (
            <p className="progress-empty-text">ครบถ้วนทุกสาขาแล้ว</p>
          ) : (
            <div className="progress-chip-list">
              {pending.map(({ branch }) => (
                <button
                  key={branch.id}
                  type="button"
                  className="progress-chip pending"
                  onClick={() => {
                    onSelectBranch(branch.id);
                    onClose();
                  }}
                >
                  <div>
                    <strong>{branch.name}</strong>
                    <span>{branch.region}</span>
                  </div>
                  <IconChevronRight />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="progress-col completed-col">
          <div className="progress-col-header">
            <IconCheck className="col-icon completed" />
            <h3>อัปเดตวันนี้แล้ว ({completed.length})</h3>
          </div>
          {completed.length === 0 ? (
            <p className="progress-empty-text">ยังไม่มีสาขาที่อัปเดตวันนี้</p>
          ) : (
            <div className="progress-chip-list">
              {completed.map(({ branch, label }) => (
                <button
                  key={branch.id}
                  type="button"
                  className="progress-chip completed"
                  onClick={() => {
                    onSelectBranch(branch.id);
                    onClose();
                  }}
                >
                  <div>
                    <strong>{branch.name}</strong>
                    <span>{label}</span>
                  </div>
                  <IconChevronRight />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function NavButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return <button className={active ? 'nav-button active' : 'nav-button'} onClick={() => { haptic(10); onClick(); }}>{icon}<span>{label}</span></button>;
}

type Store = ReturnType<typeof useAppStore>;

function SalesPage({ activeBranches, saleFor, saveSale, today, reportSubmitted, onReset, onCopy, isSubmitting }: Store & { onReset: () => void; onCopy: () => void; isSubmitting: boolean }) {
  return <section className="page sales-page">
    <div className="page-title"><div><span className="eyebrow">งานประจำวัน</span><h1>เช็กยอดทุกสาขา</h1><p>ข้อมูลบันทึกลงเครื่องอัตโนมัติ ใช้งานต่อได้แม้ออฟไลน์</p></div>{activeBranches.length > 0 && <button className="button ghost compact" onClick={onReset}><IconRefresh />ล้างยอด</button>}</div>
    {!activeBranches.length ? <EmptyState icon={<IconBuildingStore />} title="ยังไม่มีสาขา" detail="เริ่มจากเพิ่มสาขาแรก แล้วกลับมากรอกยอดประจำวัน" /> : <div className="sales-grid">{activeBranches.map((branch, index) => <BranchCard key={branch.id} branch={branch} index={index} sale={saleFor(branch)} today={today} saveSale={saveSale} />)}</div>}
    {activeBranches.length > 0 && <div className="copy-dock"><div><span>ยอดรวมวันนี้</span><strong>฿{money.format(activeBranches.reduce((sum, branch) => sum + (saleFor(branch).salesAmount ?? 0), 0))}</strong></div><button className="button primary copy-btn" disabled={isSubmitting} onClick={onCopy}>{isSubmitting ? <><IconRefresh />กำลังส่ง...</> : <><IconCopy />{reportSubmitted ? 'คัดลอกและอัปเดตรายงาน' : 'คัดลอกและส่งรายงาน'}</>}</button></div>}
  </section>;
}

const checks: Array<{ key: keyof DailySale; short: string; icon: React.ReactNode }> = [
  { key: 'paidComplete', short: 'โอน', icon: <IconCash /> }, { key: 'posComplete', short: 'POS', icon: <IconDeviceDesktop /> },
  { key: 'billComplete', short: 'บิล', icon: <IconReceipt /> }, { key: 'stockAttached', short: 'สต๊อก', icon: <IconPackage /> },
  { key: 'salesLinkUpdated', short: 'ลิงก์', icon: <IconLink /> }, { key: 'dailyInspectionAttached', short: 'ใบตรวจ', icon: <IconClipboardCheck /> }
];

function BranchCard({ branch, index, sale, today, saveSale }: { branch: Branch; index: number; sale: DailySale; today: string; saveSale: Store['saveSale'] }) {
  const status = getSaleStatus(sale, today);
  const completed = checks.filter((item) => Boolean(sale[item.key])).length + Number(sale.unpluggedPhotoConfirmed);
  return <article id={`branch-${branch.id}`} className={completed === 7 && sale.salesAmount !== null ? 'branch-card complete' : 'branch-card'}>
    <div className="branch-heading">
      <div className="branch-number">{String(index + 1).padStart(2, '0')}</div>
      <div className="branch-header-content">
        <div className="branch-header-top">
          <h2>{branch.name}</h2>
          <div className="branch-meta-right">
            <span className={`status-pill ${status.isToday ? 'updated' : 'pending'}`}>
              {status.isToday ? <IconCheck /> : <IconAlertCircle />}
              <span>{status.label}</span>
            </span>
            <div className="completion"><strong>{completed}/7</strong></div>
          </div>
        </div>
        <span className="branch-region">{branch.region}</span>
      </div>
    </div>
    <label className="amount-field"><span>ยอดขายวันนี้</span><div><b>฿</b><input inputMode="decimal" type="number" min="0" placeholder="0" value={sale.salesAmount ?? ''} onFocus={(event) => event.target.select()} onClick={(event) => (event.target as HTMLInputElement).select()} onChange={(event) => { const value = event.target.value; const amount = Number(value); void saveSale(branch, { salesAmount: value === '' || !Number.isFinite(amount) || amount < 0 ? null : amount }, 'sale_changed'); }} /></div></label>
    <div className="check-grid">{checks.map((item) => <CheckPill key={String(item.key)} checked={Boolean(sale[item.key])} label={item.short} icon={item.icon} onChange={(checked) => void saveSale(branch, { [item.key]: checked }, 'checklist_changed')} />)}
      <CheckPill checked={sale.unpluggedPhotoConfirmed} label="ถอดปลั๊ก" icon={<IconPlug />} onChange={(checked) => void saveSale(branch, { unpluggedPhotoConfirmed: checked }, 'checklist_changed')} />
    </div>
  </article>;
}

function CheckPill({ checked, label, icon, full, onChange }: { checked: boolean; label: string; icon: React.ReactNode; full?: boolean; onChange: (value: boolean) => void }) {
  const handleClick = () => {
    haptic(15);
    onChange(!checked);
  };
  return <button type="button" className={`check-pill ${checked ? 'checked' : ''} ${full ? 'full' : ''}`} aria-pressed={checked} onClick={handleClick}><span className="pill-icon">{checked ? <IconSquareRoundedCheck /> : icon}</span><span>{label}</span></button>;
}

function formatMonthName(month: string) {
  return new Intl.DateTimeFormat('th-TH', { month: 'long', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(new Date(`${month}-15T12:00:00+07:00`));
}

function HistoryPage({ sales, branches, today }: { sales: DailySale[]; branches: Branch[]; today: string }) {
  const months = useMemo(() => [...new Set([monthKey(today), ...sales.map((sale) => monthKey(sale.salesDate))])].sort().reverse(), [sales, today]);
  const [selected, setSelected] = useState(months[0]);
  const [showPicker, setShowPicker] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});

  const toggleRegion = (region: string) => {
    haptic(12);
    setExpandedRegions((prev) => ({
      ...prev,
      [region]: !prev[region],
    }));
  };

  const currentIndex = months.indexOf(selected);
  const rows = sales.filter((sale) => monthKey(sale.salesDate) === selected && sale.salesAmount !== null);
  const dates = [...new Set(rows.map((row) => row.salesDate))].sort().reverse();
  const regions = totalsByRegion(rows);
  const total = rows.reduce((sum, row) => sum + (row.salesAmount ?? 0), 0);

  const branchesByRegion = useMemo(() => {
    const map: Record<string, Array<{ branchId: string; name: string; amount: number; days: number }>> = {};

    for (const [region] of regions) {
      const branchMap = new Map<string, { branchId: string; name: string; amount: number; days: number }>();

      for (const row of rows) {
        if (row.regionSnapshot === region) {
          const bId = row.branchId;
          const current = branchMap.get(bId);
          if (current) {
            current.amount += row.salesAmount ?? 0;
            current.days += 1;
          } else {
            const bObj = branches.find((b) => b.id === bId);
            branchMap.set(bId, {
              branchId: bId,
              name: row.branchNameSnapshot || bObj?.name || 'ไม่ระบุสาขา',
              amount: row.salesAmount ?? 0,
              days: 1,
            });
          }
        }
      }

      for (const b of branches) {
        if (b.region === region && b.isActive && !branchMap.has(b.id)) {
          branchMap.set(b.id, {
            branchId: b.id,
            name: b.name,
            amount: 0,
            days: 0,
          });
        }
      }

      map[region] = Array.from(branchMap.values()).sort((a, b) => b.amount - a.amount);
    }

    return map;
  }, [regions, rows, branches]);

  return (
    <section className="page history-page">
      <div className="page-title">
        <div>
          <span className="eyebrow">ภาพรวมย้อนหลัง</span>
          <h1>ประวัติยอดขาย</h1>
          <p>ดูแนวโน้มรายวันและยอดรวมแยกตามภูมิภาค</p>
        </div>

        <div className="month-stepper">
          <button
            type="button"
            className="month-stepper-btn"
            disabled={currentIndex >= months.length - 1}
            onClick={() => {
              haptic(12);
              setSelected(months[currentIndex + 1]);
            }}
            aria-label="เดือนก่อนหน้า"
            title="เดือนก่อนหน้า"
          >
            <IconChevronLeft />
          </button>

          <button
            type="button"
            className="month-stepper-label"
            onClick={() => {
              haptic(12);
              setShowPicker(true);
            }}
            aria-label="เลือกเดือน"
          >
            <IconCalendarStats />
            <span>{formatMonthName(selected)}</span>
            <IconChevronDown />
          </button>

          <button
            type="button"
            className="month-stepper-btn"
            disabled={currentIndex <= 0}
            onClick={() => {
              haptic(12);
              setSelected(months[currentIndex - 1]);
            }}
            aria-label="เดือนถัดไป"
            title="เดือนถัดไป"
          >
            <IconChevronRight />
          </button>
        </div>
      </div>

      {!rows.length ? (
        <EmptyState
          icon={<IconCalendarStats />}
          title="ยังไม่มีประวัติในเดือนนี้"
          detail="เมื่อกรอกยอดแล้ว ข้อมูลประจำวันจะแสดงที่นี่"
        />
      ) : (
        <>
          <div className="summary-grid">
            <SummaryCard label="ยอดรวมเดือนนี้" value={`฿${money.format(total)}`} icon={<IconCash />} tone="violet" />
            <SummaryCard label="วันที่มีข้อมูล" value={`${dates.length} วัน`} icon={<IconCalendarStats />} tone="blue" />
            <SummaryCard label="ภูมิภาค" value={`${regions.length} โซน`} icon={<IconBuildingStore />} tone="mint" />
          </div>
          <div className="panel">
            <div className="panel-title">
              <div>
                <span className="eyebrow">ยอดตามภูมิภาค</span>
                <h2>สัดส่วนยอดขาย</h2>
              </div>
              <span className="region-hint-badge">แตะเพื่อดูอันดับสาขา</span>
            </div>
            <div className="region-list">
              {regions.map(([region, amount]) => {
                const isOpen = !!expandedRegions[region];
                const regionBranches = branchesByRegion[region] || [];
                const pctOfTotal = total > 0 ? Math.round((amount / total) * 100) : 0;

                return (
                  <div className={`region-card ${isOpen ? 'open' : ''}`} key={region}>
                    <button
                      type="button"
                      className="region-header-btn"
                      onClick={() => toggleRegion(region)}
                      aria-expanded={isOpen}
                    >
                      <div className="region-header-top">
                        <div className="region-name-group">
                          <strong>{region}</strong>
                          <span className="region-branch-count">{regionBranches.length} สาขา</span>
                        </div>
                        <div className="region-amount-group">
                          <strong>฿{money.format(amount)}</strong>
                          <small>({pctOfTotal}%)</small>
                          <IconChevronDown className="region-chevron" />
                        </div>
                      </div>
                      <div className="bar">
                        <i style={{ width: `${total ? (amount / total) * 100 : 0}%` }} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="region-branches-list">
                        {!regionBranches.length ? (
                          <div className="region-branch-empty">ไม่มีข้อมูลสาขาในภูมิภาคนี้</div>
                        ) : (
                          regionBranches.map((branch, idx) => {
                            const branchPct = amount > 0 ? Math.round((branch.amount / amount) * 100) : 0;
                            const maxBranchAmt = regionBranches[0]?.amount || 1;
                            const barWidth = maxBranchAmt > 0 ? Math.round((branch.amount / maxBranchAmt) * 100) : 0;
                            const rankClass = idx === 0 ? 'top-1' : idx === 1 ? 'top-2' : idx === 2 ? 'top-3' : '';

                            return (
                              <div className="region-branch-item" key={branch.branchId}>
                                <div className="region-branch-info">
                                  <div className="region-branch-left">
                                    <span className={`rank-badge ${rankClass}`}>{idx + 1}</span>
                                    <span className="region-branch-name">สาขา{branch.name}</span>
                                    {branch.days > 0 && (
                                      <small className="region-branch-days">({branch.days} วัน)</small>
                                    )}
                                  </div>
                                  <div className="region-branch-right">
                                    <strong className="region-branch-amt">฿{money.format(branch.amount)}</strong>
                                    <span className="region-branch-pct">{branchPct}%</span>
                                  </div>
                                </div>
                                <div className={`branch-bar ${idx === 0 ? 'top-1' : ''}`}>
                                  <i style={{ width: `${barWidth}%` }} />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="panel">
            <div className="panel-title">
              <div>
                <span className="eyebrow">รายวัน</span>
                <h2>รายการที่บันทึก</h2>
              </div>
            </div>
            <div className="history-list">
              {dates.map((date) => {
                const dayRows = rows.filter((row) => row.salesDate === date);
                return (
                  <details key={date}>
                    <summary>
                      <div>
                        <strong>{thaiDisplayDate(date)}</strong>
                        <span>{dayRows.length} สาขา</span>
                      </div>
                      <b>฿{money.format(dayRows.reduce((sum, row) => sum + (row.salesAmount ?? 0), 0))}</b>
                      <IconChevronRight />
                    </summary>
                    <div className="day-rows">
                      {dayRows.map((row) => (
                        <div key={row.id}>
                          <span>
                            {row.branchNameSnapshot}
                            <small>{row.regionSnapshot}</small>
                          </span>
                          <strong>฿{money.format(row.salesAmount ?? 0)}</strong>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        </>
      )}

      {showPicker && (
        <Modal onClose={() => setShowPicker(false)} compact>
          <div className="modal-heading">
            <div>
              <span className="eyebrow">เลือกช่วงเวลา</span>
              <h2>เลือกเดือน</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() => setShowPicker(false)}
              aria-label="ปิด"
            >
              <IconX />
            </button>
          </div>

          <div className="month-picker-list">
            {months.map((m) => {
              const isCurrent = m === selected;
              const mSales = sales.filter((s) => monthKey(s.salesDate) === m && s.salesAmount !== null);
              const mTotal = mSales.reduce((sum, s) => sum + (s.salesAmount ?? 0), 0);
              const mDays = new Set(mSales.map((s) => s.salesDate)).size;
              const isThisMonth = m === monthKey(today);

              return (
                <button
                  key={m}
                  type="button"
                  className={`month-picker-item ${isCurrent ? 'active' : ''}`}
                  onClick={() => {
                    haptic(15);
                    setSelected(m);
                    setShowPicker(false);
                  }}
                >
                  <div className="month-item-main">
                    <span className="month-item-icon">
                      <IconCalendarStats />
                    </span>
                    <div>
                      <div className="month-item-title">
                        <strong>{formatMonthName(m)}</strong>
                        {isThisMonth && <span className="month-item-badge">เดือนนี้</span>}
                      </div>
                      <small className="month-item-sub">
                        {mDays > 0 ? `${mDays} วันที่มีบันทึก • ฿${money.format(mTotal)}` : 'ยังไม่มีรายการขาย'}
                      </small>
                    </div>
                  </div>
                  {isCurrent && <IconCheck className="month-item-check" />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </section>
  );
}

function SummaryCard({ label, value, icon, tone }: { label: string; value: string; icon: React.ReactNode; tone: string }) { return <div className={`summary-card ${tone}`}><span>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></div>; }

function NotificationPanel({ branches, notify }: { branches: Branch[]; notify: (message: string) => void }) {
  const [state, setState] = useState<PushState>('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void readPushState().then(setState).catch(() => setState('available'));
  }, []);

  useEffect(() => {
    if (state !== 'enabled') return;
    void syncPushSubscription(branches).catch(() => undefined);
  }, [branches, state]);

  const enable = async () => {
    setBusy(true);
    try {
      const next = await enablePushNotifications(branches);
      setState(next);
      notify(next === 'enabled' ? 'เปิดแจ้งเตือนแล้ว' : next === 'denied' ? 'เบราว์เซอร์ไม่อนุญาตการแจ้งเตือน' : 'อุปกรณ์นี้ไม่รองรับการแจ้งเตือน');
    } catch (error) {
      notify(error instanceof Error && error.message.includes('not configured') ? 'ระบบแจ้งเตือนยังไม่ได้ตั้งค่าบนเซิร์ฟเวอร์' : 'เปิดแจ้งเตือนไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePushNotifications();
      setState('available');
      notify('ปิดแจ้งเตือนแล้ว');
    } catch {
      setState(await readPushState());
      notify('ปิดการแจ้งเตือนบนเซิร์ฟเวอร์ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const copy = state === 'enabled'
    ? { title: 'เปิดแจ้งเตือนอยู่', detail: 'เตือนเวลา 19:00 น. และแจ้งสาขาที่ยังไม่ส่งยอดอีกครั้งเวลา 19:30 น.' }
    : state === 'denied'
      ? { title: 'การแจ้งเตือนถูกปิดกั้น', detail: 'เปิดสิทธิ์ Notifications ในการตั้งค่าเบราว์เซอร์ แล้วกลับมาลองอีกครั้ง' }
      : state === 'unsupported'
        ? { title: 'อุปกรณ์นี้ยังไม่รองรับ', detail: 'บน iPhone/iPad ต้องเพิ่มเว็บลงหน้าจอโฮมก่อนจึงจะเปิด Web Push ได้' }
      : { title: 'แจ้งเตือนส่งยอด', detail: 'เตือนส่งยอด, เตือนสาขาที่ยังไม่ได้ส่ง' };

  return <div className="panel notification-panel">
    <div className="notification-copy"><span className={`title-icon notification ${state === 'enabled' ? 'active' : ''}`}>{state === 'enabled' ? <IconBell /> : <IconBellOff />}</span><div><span className="eyebrow">แจ้งเตือนประจำวัน</span><h2>{copy.title}</h2><p>{copy.detail}</p></div></div>
    {state !== 'unsupported' && state !== 'denied' && <button className={`button ${state === 'enabled' ? 'secondary' : 'primary'}`} disabled={busy || state === 'loading'} onClick={() => void (state === 'enabled' ? disable() : enable())}>{state === 'enabled' ? <><IconBellOff />ปิดแจ้งเตือน</> : <><IconBell />{busy ? 'กำลังเปิด...' : 'เปิดแจ้งเตือน'}</>}</button>}
  </div>;
}

function ManagePage({ activeBranches, addBranch, updateBranch, archiveBranch, moveBranch, clearEverything, notify, ask }: Store & { notify: (message: string) => void; ask: (value: { title: string; detail: string; action: () => void } | null) => void }) {
  const [name, setNameState] = useState(() => sessionStorage.getItem('daily-new-branch-name') ?? '');
  const [region, setRegionState] = useState<Region>(() => {
    const saved = sessionStorage.getItem('daily-new-branch-region');
    return REGIONS.includes(saved as Region) ? saved as Region : 'เหนือ';
  });
  const [editing, setEditingState] = useState<Branch | null>(() => {
    const editingId = sessionStorage.getItem('daily-editing-branch-id');
    return activeBranches.find((branch) => branch.id === editingId) ?? null;
  });
  const setEditing = (branch: Branch | null) => {
    if (branch) sessionStorage.setItem('daily-editing-branch-id', branch.id);
    else sessionStorage.removeItem('daily-editing-branch-id');
    setEditingState(branch);
  };
  const setName = (value: string) => { sessionStorage.setItem('daily-new-branch-name', value); setNameState(value); };
  const setRegion = (value: Region) => { sessionStorage.setItem('daily-new-branch-region', value); setRegionState(value); };
  const submit = async () => { if (!name.trim()) return notify('กรอกชื่อสาขาก่อน'); if (activeBranches.some((item) => item.name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase())) return notify('ชื่อสาขานี้มีอยู่แล้ว'); await addBranch(name, region); setName(''); notify('เพิ่มสาขาแล้ว'); };
  return <section className="page manage-page"><div className="page-title"><div><span className="eyebrow">ตั้งค่าระบบ</span><h1>จัดการสาขา</h1><p>เพิ่ม เรียงลำดับ และดูแลสาขาที่ใช้งานอยู่</p></div></div>
    <div className="manage-layout"><div className="panel add-panel"><div className="panel-title"><div className="title-icon"><IconPlus /></div><div><span className="eyebrow">สาขาใหม่</span><h2>เพิ่มเข้ารายการ</h2></div></div><label className="field"><span>ชื่อสาขา (ไม่ต้องใส่คำว่า “สาขา”)</span><input value={name} maxLength={50} placeholder="เช่น นครศรีฯ" onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }} /></label><label className="field"><span>ภูมิภาค</span><select value={region} onChange={(event) => setRegion(event.target.value as Region)}>{REGIONS.map((item) => <option key={item}>{item}</option>)}</select></label><button className="button primary full" onClick={() => void submit()}><IconPlus />เพิ่มสาขา</button></div>
      <div className="panel branch-panel"><div className="panel-title"><div><span className="eyebrow">{activeBranches.length} สาขา</span><h2>ลำดับการแสดงผล</h2></div></div>{!activeBranches.length ? <EmptyState compact icon={<IconBuildingStore />} title="ยังไม่มีสาขา" detail="เพิ่มสาขาจากแบบฟอร์มเพื่อเริ่มต้น" /> : <div className="branch-list">{activeBranches.map((branch, index) => <div className="branch-item" key={branch.id}><span className="order">{index + 1}</span><div className="branch-copy"><strong>{branch.name}</strong><span>{branch.region}</span></div><div className="branch-actions"><button disabled={index === 0} onClick={() => void moveBranch(branch.id, -1)} aria-label="เลื่อนขึ้น"><IconArrowUp /></button><button disabled={index === activeBranches.length - 1} onClick={() => void moveBranch(branch.id, 1)} aria-label="เลื่อนลง"><IconArrowDown /></button><button className="edit-icon" onClick={() => setEditing(branch)} aria-label="แก้ไข"><IconEdit /></button><button className="danger-icon" onClick={() => ask({ title: `ปิดใช้งาน “${branch.name}”?`, detail: 'สาขาจะหายจากหน้ายอดวันนี้ แต่ประวัติเดิมยังอยู่ครบ', action: () => void archiveBranch(branch.id).then(() => notify('ปิดใช้งานสาขาแล้ว')) })} aria-label="ปิดใช้งาน"><IconTrash /></button></div></div>)}</div>}</div>
    </div>
    <NotificationPanel branches={activeBranches} notify={notify} />
    <button className="button danger local-clear-button" onClick={() => ask({ title: 'ล้างข้อมูลทั้งหมดในเครื่อง?', detail: 'การกระทำนี้ลบรายชื่อสาขา ยอด และคิวที่ยังไม่ส่งออกจากอุปกรณ์นี้', action: () => void clearEverything().then(() => notify('ล้างข้อมูลในเครื่องแล้ว')) })}><IconTrash />ล้างข้อมูลในเครื่อง</button>
    {editing && <EditBranch branch={editing} onClose={() => setEditing(null)} onSave={(branch) => void updateBranch(branch).then(() => { setEditing(null); notify('บันทึกการแก้ไขแล้ว'); })} />}
  </section>;
}

function EditBranch({ branch, onClose, onSave }: { branch: Branch; onClose: () => void; onSave: (branch: Branch) => void }) {
  const draftKey = `daily-edit-branch:${branch.id}`;
  const savedDraft = (() => { try { return JSON.parse(sessionStorage.getItem(draftKey) ?? 'null') as { name?: string; region?: Region } | null; } catch { return null; } })();
  const [name, setName] = useState(savedDraft?.name ?? branch.name); const [region, setRegion] = useState(savedDraft?.region ?? branch.region);
  useEffect(() => { sessionStorage.setItem(draftKey, JSON.stringify({ name, region })); }, [draftKey, name, region]);
  const close = () => { sessionStorage.removeItem(draftKey); onClose(); };
  const save = () => { sessionStorage.removeItem(draftKey); onSave({ ...branch, name: name.trim(), region }); };
  return <Modal onClose={close}><div className="modal-heading"><div><span className="eyebrow">แก้ไขสาขา</span><h2>{branch.name}</h2></div><button className="icon-button" onClick={close}><IconX /></button></div><label className="field"><span>ชื่อสาขา</span><input value={name} onChange={(event) => setName(event.target.value)} /></label><label className="field"><span>ภูมิภาค</span><select value={region} onChange={(event) => setRegion(event.target.value as Region)}>{REGIONS.map((item) => <option key={item}>{item}</option>)}</select></label><button className="button primary full" disabled={!name.trim()} onClick={save}><IconCheck />บันทึก</button></Modal>;
}

function EmptyState({ icon, title, detail, compact }: { icon: React.ReactNode; title: string; detail: string; compact?: boolean }) { return <div className={compact ? 'empty compact' : 'empty'}><span>{icon}</span><h2>{title}</h2><p>{detail}</p></div>; }
function Modal({ children, onClose, wide, compact }: { children: React.ReactNode; onClose: () => void; wide?: boolean; compact?: boolean }) { return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className={`modal ${wide ? 'wide' : ''} ${compact ? 'compact' : ''}`}>{children}</div></div>; }

